import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, Mail, Github, Linkedin } from 'lucide-react';

const Hero = () => {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);

  const phrases = React.useMemo(() => ['Computer Science Student', 'Frontend Developer', 'Java Enthusiast', 'React Developer'], []);

  useEffect(() => {
    const handleTyping = () => {
      const i = loopNum % phrases.length;
      const fullText = phrases[i];

      setText(
        isDeleting
          ? fullText.substring(0, text.length - 1)
          : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 50 : 150);

      if (!isDeleting && text === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed, phrases]);

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center px-6 pt-20 relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left Content */}
        <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
          <div className="space-y-2">
            <p className="text-teal-500 dark:text-teal-400 font-medium text-lg">Hello, I'm</p>
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 dark:text-white">
              Chandana Vadathala
            </h1>
            <div className="h-12">
              <h2 className="text-2xl md:text-3xl font-medium text-slate-700 dark:text-slate-300">
                {text}
                <span className="animate-pulse">|</span>
              </h2>
            </div>
          </div>

          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
            Passionate about building real-world web applications with strong fundamentals in Java
            and frontend development. Currently seeking opportunities to contribute to impactful
            projects.
          </p>

          <div className="flex flex-wrap gap-4 pt-4">
            <Button
              size="lg"
              className="bg-teal-500 hover:bg-teal-600 text-white font-medium transition-all hover:scale-105"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              <Mail className="mr-2 h-5 w-5" />
              Get In Touch
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-teal-500 text-teal-500 hover:bg-teal-500 hover:text-white transition-all hover:scale-105"
            >
              <Download className="mr-2 h-5 w-5" />
              Download Resume
            </Button>
          </div>

          {/* Social Links */}
          <div className="flex gap-4 pt-4">
            <a
              href="https://github.com/chandanavadathala"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-teal-500 hover:text-white transition-all hover:scale-110"
            >
              <Github className="h-5 w-5" />
            </a>
            <a
              href="https://www.linkedin.com/in/chandana-vadathala-b7298a294"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-teal-500 hover:text-white transition-all hover:scale-110"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Right Content - Profile Image */}
        <div className="flex justify-center animate-in fade-in slide-in-from-right duration-700">
          <div className="relative">
            <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="relative w-80 h-80 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-teal-500/50 shadow-2xl hover:scale-105 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_chandana-portfolio-1/artifacts/msaqx5e3_pic.jpg"
                alt="Chandana Vadathala"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
